import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymMarketingCardInner,
  gymMarketingMainCol,
  gymMarketingSideCol,
  gymMarketingSplitGrid,
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';
import GymEmailCampaignEditor from './GymEmailCampaignEditor';

export default function GymEmailTemplatesPanel({ api, onApplyTemplate }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('gym_email_templates.php?action=list', { baseURL: emailApi });
      setTemplates(res.data?.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.templates.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('gym.email.templates.nameRequired'));
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error(t('gym.email.templates.bodyRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.post(
        'gym_email_templates.php?action=create',
        { name: name.trim(), subject: subject.trim(), body_html: bodyHtml },
        { baseURL: emailApi }
      );
      toast.success(t('gym.email.templates.created'));
      setName('');
      setSubject('');
      setBodyHtml('');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.templates.createError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('gym.email.templates.deleteConfirm'))) {
      return;
    }
    try {
      await api.post('gym_email_templates.php?action=delete', { id }, { baseURL: emailApi });
      toast.success(t('gym.email.templates.deleted'));
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.templates.deleteError'));
    }
  };

  const handleApply = async (id) => {
    try {
      const res = await api.get(`gym_email_templates.php?action=get&id=${id}`, { baseURL: emailApi });
      const tpl = res.data?.template;
      if (!tpl) {
        return;
      }
      onApplyTemplate?.({
        subject: tpl.subject || '',
        bodyHtml: tpl.body_html || '',
      });
      toast.success(t('gym.email.templates.applied'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.templates.loadError'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={gymMarketingSplitGrid}>
      <div className={gymMarketingMainCol}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner} space-y-5`}>
          <div>
            <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>{t('gym.email.templates.title')}</h2>
            <p className={`text-sm mt-2 leading-relaxed ${gymSocialMutedText}`}>{t('gym.email.templates.hint')}</p>
          </div>

          <form onSubmit={handleCreate} className="space-y-5">
            <label className="block">
              <span className={gymModalLabel}>{t('gym.email.templates.name')}</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`mt-2 w-full ${gymModalInput}`}
                maxLength={120}
                placeholder={t('gym.email.templates.namePlaceholder')}
              />
            </label>
            <label className="block">
              <span className={gymModalLabel}>{t('gym.email.campaigns.subject')}</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={`mt-2 w-full ${gymModalInput}`}
                maxLength={255}
              />
            </label>
            <GymEmailCampaignEditor value={bodyHtml} onChange={setBodyHtml} disabled={saving} />
            <button type="submit" disabled={saving} className={`${gymSocialBtnPrimary} inline-flex items-center gap-2`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('gym.email.templates.save')}
            </button>
          </form>
        </div>
      </div>

      <div className={gymMarketingSideCol}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner} flex flex-col min-h-[280px]`}>
          <h3 className={`text-sm font-semibold ${gymSocialTitleText}`}>{t('gym.email.templates.savedTitle')}</h3>
          <p className={`text-xs mt-1.5 mb-4 leading-relaxed ${gymSocialMutedText}`}>
            {t('gym.email.templates.savedHint')}
          </p>

          {templates.length === 0 ? (
            <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.templates.empty')}</p>
          ) : (
            <ul className="space-y-2 flex-1 min-h-0 overflow-y-auto max-h-[min(70vh,640px)] pr-1 -mr-1">
              {templates.map((tpl) => (
                <li
                  key={tpl.id}
                  className="rounded-lg border border-gray-200/90 bg-white/60 dark:border-white/10 dark:bg-gray-800/40 p-4"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <FileText className="h-5 w-5 shrink-0 opacity-70 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium text-sm ${gymSocialTitleText}`}>{tpl.name}</p>
                      {tpl.subject && (
                        <p className={`text-xs mt-1 truncate ${gymSocialMutedText}`}>{tpl.subject}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200/70 dark:border-white/10">
                    <button type="button" onClick={() => handleApply(tpl.id)} className={`${gymSocialBtnPrimary} text-xs py-1.5 px-3`}>
                      {t('gym.email.templates.use')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(tpl.id)}
                      className={`${gymSocialBtnSecondary} inline-flex items-center gap-1 text-xs py-1.5 px-3 text-red-700 dark:text-red-300`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
